import { createBrowserRouter } from 'react-router-dom'
import { NotFoundPage } from './pages/NotFoundPage'
import { AdminRoutes } from './AdminRoutes'
import { SuperUserRoutes } from './SuperUserRoutes'
import { StoreRoutes } from './StoreRoutes'
import { EmbedRoutes } from './EmbedRoutes'
import { markInternalNavigation } from './config/navigation-source'

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  // Denormalized ticket page — isolated from the rest of the app
  { path: '/ticket/:ticketId', lazy: () => import('./pages/ticket.$ticketId') },
  // Barebones layout holds the QueryClientProvider the resolver needs
  {
    path: '/to/:token',
    lazy: () => import('./pages/_barebonesLayout'),
    children: [{ index: true, lazy: () => import('./pages/to.$token') }],
  },

  { path: '/store/:oid/embed',
    lazy: () => import('./pages/_barebonesLayout'),
    children: [
      // Public chrome-less iframe widget; logged-out visitors render it
      {path: ':wid', lazy: () => import('./pages/store.$oid.embed.$wid')},
    ]
  },
  {
    path: '/', lazy: () => import('./pages/_index'),
    children: [
      // Iframe embeds — no layouts, nested only deep enough for context
      { path: '__/auth/action', lazy: () => import('./pages/__.auth.action') },
      // Marketing pages under shared layout
      {
        path: '',
        lazy: () => import('./pages/_marketing'),
        children: [
          { path: '', lazy: () => import('./pages/index') },
          { path: 'features', lazy: () => import('./pages/features') },
          { path: 'pricing', lazy: () => import('./pages/pricing') },
          { path: 'how-it-works', lazy: () => import('./pages/how-it-works') },
          { path: 'about', lazy: () => import('./pages/about') },
          // Careers disabled — unregistered so it 404s; pages/careers.tsx stays
          { path: 'for', lazy: () => import('./pages/for') },
          { path: 'for/:vertical', lazy: () => import('./pages/for.$vertical') },
          { path: 'support', lazy: () => import('./pages/support') },
          { path: 'support/:slug', lazy: () => import('./pages/support.$slug') },
          { path: 'support/:category/:slug', lazy: () => import('./pages/support.$category.$slug') },
        ],
      },
      { path: 'organization-privacy-policy', lazy: () => import('./pages/organization-privacy-policy') },
      { path: 'organization-tos', lazy: () => import('./pages/organization-tos') },
      { path: 'my-tickets', lazy: () => import('./pages/my-tickets') },
      { path: 'getting-started', lazy: () => import('./pages/getting-started') },
      { path: 'staff/:oid/schedule/:uid', lazy: () => import('./pages/staff.$oid.schedule.$uid') },
      { path: 'my-tickets', lazy: () => import('./pages/my-tickets') },
      { path: 'staff',
        children: [
          { path: ':oid/schedule/:uid', lazy: () => import('./pages/staff.$oid.schedule.$uid')}
        ]
      },
      {
        path: '/embed',
        lazy: () => import('./pages/_barebonesLayout'),
        children: EmbedRoutes
      },
      {
        path: 'store',
        lazy: () => import('./pages/_store'),
        children: StoreRoutes,
      },
      {
        path: 'admin',
        children: [
          { path: '', lazy: () => import('./pages/admin') },
          { path: 'create-organization', lazy: () => import('./pages/admin.create-new-organization') },
          {
            path: 'su',
            lazy: () => import('./pages/admin._su'),
            handle: { layout: true },
            children: SuperUserRoutes
          },
          {
            path: ':oid',
            lazy: () => import('./pages/admin._$oid'),
            handle: { layout: true },
            children: AdminRoutes,
          },
        ],
      }
    ]
  },
  { path: '*', element: <NotFoundPage goBackPath='/' goBackText='Go back home' /> },
])

// Only PUSH = a real in-app link; entry redirects REPLACE
router.subscribe(({ historyAction }) => {
  if (historyAction === 'PUSH') markInternalNavigation()
})
